import React, { useEffect, useState } from 'react';
import { Alert, Box, CircularProgress, Typography } from '@mui/material';
import HubIcon from '@mui/icons-material/Hub';
import { useTranslation } from 'react-i18next';

import NetworkFeedSelect from '../network/NetworkFeedSelect';
import {
  getCompanyDefaultNetwork,
  setCompanyDefaultNetwork,
} from '../../services/network/networkPreferenceService';
import { readAxiosDetail } from './api_calls';
import { trackAnalyticsEvent } from '../../services/analytics';
import { ANALYTICS_EVENT_NAMES } from '../../services/eventTaxonomy';

/**
 * Company-admin setting: the org-wide default transit network. It cascades to every
 * user in the company who hasn't chosen a personal default (resolved server-side as
 * user → customer → none). Company-admin only — enforced by the HTTM endpoint.
 */
export default function CompanyNetworkSettingsTab() {
  const { t } = useTranslation('company');
  const [feedId, setFeedId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    getCompanyDefaultNetwork()
      .then((v) => {
        if (active) setFeedId(v);
      })
      .catch((e) => {
        if (active) setError(readAxiosDetail(e));
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, []);

  const handleChange = async (next: string | null) => {
    const previous = feedId;
    setFeedId(next); // optimistic
    setSaving(true);
    setError(null);
    try {
      const saved = await setCompanyDefaultNetwork(next);
      setFeedId(saved);
      trackAnalyticsEvent(ANALYTICS_EVENT_NAMES.default_network_changed, {
        scope: 'customer',
        feed_id: next,
        timestamp: new Date().toISOString(),
      });
    } catch (e) {
      setFeedId(previous); // revert on failure
      setError(readAxiosDetail(e));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 520 }}>
      <Typography
        variant="subtitle1"
        fontWeight={600}
        gutterBottom
        sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
      >
        <HubIcon fontSize="small" sx={{ color: '#ef7335' }} />
        {t('network.title')}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {t('network.description')}
      </Typography>
      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      <NetworkFeedSelect
        label={t('network.selectLabel')}
        value={feedId}
        onChange={handleChange}
        disabled={saving}
        noneLabel={t('network.none')}
        helperText={t('network.help')}
      />
    </Box>
  );
}
